/**
 * The route table (PLAN §6.5, §10.1): every REST route of the rundown API with
 * its method, path, schemas, success status, required permission and the
 * error codes it can answer with.
 *
 * It is the source for three consumers, so they cannot drift apart:
 *   - handlers (cloud Lambda and node Fastify) validate with its schemas and
 *     check its permission;
 *   - `openapi.ts` generates `openapi.json` from it (`pnpm docs:openapi`);
 *   - the `acp` CLI and the SPA client (planned) type their calls from it.
 *
 * The tenancy routes (`/me`, tenants, users, groups) live in `tenancyRoutes.ts`,
 * the cloud's status view (`/status`) in `statusRoutes.ts`, studios, productions and
 * playout in `operationsRoutes.ts`, sites, nodes and enrolment in `sitesRoutes.ts`,
 * offline auth in `offlineAuthRoutes.ts`, a node's replication routes in `nodeSync.ts`;
 * all are spread in below.
 *
 * Each entry keeps its concrete zod types (the `route` helper is an identity
 * function that exists only to check the shape), so `z.infer<typeof
 * ROUTES.listRows.query>` is the handler's input type.
 */
import type { z } from 'zod';
import type { ErrorCode } from './errors.js';
import type { PermissionId } from './permissions.js';
import {
  getRowResponseSchema,
  listRowsQuerySchema,
  listRowsResponseSchema,
  patchBatchRequestSchema,
  patchBatchResponseSchema,
  rowPathSchema,
  rundownPathSchema,
} from './rows.js';
import { NODE_SYNC_ROUTES } from './nodeSync.js';
import { CLOUD_PRODUCTIONS_ROUTES } from './cloudProductionsRoutes.js';
import { OFFLINE_AUTH_ROUTES } from './offlineAuthRoutes.js';
import { OPERATIONS_ROUTES } from './operationsRoutes.js';
import { SITES_ROUTES } from './sitesRoutes.js';
import { REDUNDANCY_ROUTES } from './redundancyRoutes.js';
import { STATUS_ROUTES } from './statusRoutes.js';
import { TENANCY_ROUTES } from './tenancyRoutes.js';
import {
  createPlaylistRequestSchema,
  createRundownRequestSchema,
  getPlaylistResponseSchema,
  getRundownResponseSchema,
  listPlaylistsQuerySchema,
  listPlaylistsResponseSchema,
  listRundownsQuerySchema,
  listRundownsResponseSchema,
  playlistPathSchema,
  playlistResponseSchema,
  rundownResponseSchema,
} from './rundowns.js';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type RouteDef<
  P extends z.ZodObject | null = z.ZodObject | null,
  Q extends z.ZodObject | null = z.ZodObject | null,
  B extends z.ZodType | null = z.ZodType | null,
  R extends z.ZodType = z.ZodType,
> = {
  /** The OpenAPI operationId; stable. */
  readonly id: string;
  readonly method: HttpMethod;
  /** With `{param}` placeholders, each one a key of `params`. */
  readonly path: string;
  /** Groups routes in the docs. */
  readonly tag:
    | 'rundowns'
    | 'playlists'
    | 'rows'
    | 'tenancy'
    | 'vendor'
    | 'status'
    | 'studios'
    | 'show-styles'
    | 'productions'
    | 'playout'
    | 'sites'
    | 'node'
    | 'redundancy'
    | 'cloud-productions'
    | 'devices';
  readonly summary: string;
  readonly description: string;
  readonly params: P;
  readonly query: Q;
  readonly body: B;
  readonly response: R;
  readonly successStatus: 200 | 201;
  /** The permission the caller needs (D14). Null: authenticated only. */
  readonly permission: PermissionId | null;
  /**
   * Where `permission` must be held:
   *   - `'resource'` — on the resource's site/studio. A router admits a caller
   *     holding it in any scope, and the handler checks the resource's own
   *     location (`isPermitted` in `@acp/domain/access.js`).
   *   - `'tenant'`   — through a tenant-wide grant (tenant administration).
   */
  readonly permissionScope: 'resource' | 'tenant';
  /**
   * For a route whose permission depends on its body (the playout command
   * route: `playout.take` or `playout.activate` per command type), the
   * permissions any one of which admits a caller at the gate — `permission`
   * is then null, and the handler checks the exact one on the resource.
   * Absent for every other route.
   */
  readonly permissionOneOf?: readonly PermissionId[];
  /**
   * Who may call it. Absent means `'user'` — a Cognito user (or, on a node, a
   * local session, D20), gated by `permission`. `'node'`: an enrolled node, by
   * a signed request (`@acp/cloud-link/nodeSignature.js`, PLAN §11); a user's
   * token is refused, and `permission` must be null. `'none'`: no credential
   * at all — the route gates itself (`POST /enrol`, whose one-time code is the
   * credential). Absent for every user route, so the table's existing
   * entries read as they always did.
   */
  readonly auth?: 'node' | 'none';
  /**
   * The error codes this route answers with, beyond those every authenticated
   * route can give (`unauthorized`, `forbidden` when a permission is set,
   * `rate-limited`, `internal`, `unavailable`).
   */
  readonly errors: readonly ErrorCode[];
};

function route<P extends z.ZodObject | null, Q extends z.ZodObject | null, B extends z.ZodType | null, R extends z.ZodType>(
  def: RouteDef<P, Q, B, R>,
): RouteDef<P, Q, B, R> {
  return def;
}

const PATCHES_DESCRIPTION =
  'Merge a batch of row patches (PLAN §6.1: per-field LWW under each patch’s HLC; text fields via Yjs updates). ' +
  'Each patch is validated (schema per kind, parent rules, locked fields, permission) and merged independently; ' +
  'the response has one result per patch, in request order: `ack` with the fields that changed and the canonical row, ' +
  'or `nack` with every reason and the canonical current row to roll back to. A stale patch (older HLC on every field) ' +
  'is an `ack` with `applied: []`. The batch is not atomic across rows (PLAN §6.2). For clients without a socket and ' +
  'for the MOS gateway; the WS `row.patch` message is the same operation.';

export const ROUTES = {
  createRundown: route({
    id: 'createRundown',
    method: 'POST',
    path: '/rundowns',
    tag: 'rundowns',
    summary: 'Create a rundown',
    description:
      'Creates a rundown: the head row of a new partition, optionally placed in a playlist. Supplying `rundownId` makes a retry idempotent: a rundown with that id is returned as it is.',
    params: null,
    query: null,
    body: createRundownRequestSchema,
    response: rundownResponseSchema,
    successStatus: 201,
    permission: 'rundown.edit',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict'],
  }),
  listRundowns: route({
    id: 'listRundowns',
    method: 'GET',
    path: '/rundowns',
    tag: 'rundowns',
    summary: 'List rundowns',
    description: 'Lists the tenant’s rundowns the caller may read, optionally only those in one playlist, in pages.',
    params: null,
    query: listRundownsQuerySchema,
    body: null,
    response: listRundownsResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request'],
  }),
  getRundown: route({
    id: 'getRundown',
    method: 'GET',
    path: '/rundowns/{rundownId}',
    tag: 'rundowns',
    summary: 'Get a rundown',
    description: 'The rundown’s head row and the current cursor: pass it as `changedSince` to catch up later (PLAN §6.5).',
    params: rundownPathSchema,
    query: null,
    body: null,
    response: getRundownResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  }),
  deleteRundown: route({
    id: 'deleteRundown',
    method: 'DELETE',
    path: '/rundowns/{rundownId}',
    tag: 'rundowns',
    summary: 'Soft-delete a rundown',
    description:
      'Sets `deleted` on the head row (an ordinary LWW write, so an undelete is a patch with `deleted: false`). The rows stay; a production already running it is unaffected (PLAN §5.2).',
    params: rundownPathSchema,
    query: null,
    body: null,
    response: rundownResponseSchema,
    successStatus: 200,
    permission: 'rundown.edit',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  }),
  listRows: route({
    id: 'listRows',
    method: 'GET',
    path: '/rundowns/{rundownId}/rows',
    tag: 'rows',
    summary: 'List a rundown’s rows',
    description:
      'Every row of the rundown, soft-deleted ones included (this is the replication surface), in pages. `parentId` limits it to one parent’s direct children. ' +
      '`changedSince=<cursor>` returns only rows changed since a previous response’s `cursor` — re-reading the 5 s before it, because the index is eventually consistent, so rows can repeat: merge, do not append (PLAN §6.5).',
    params: rundownPathSchema,
    query: listRowsQuerySchema,
    body: null,
    response: listRowsResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  }),
  getRow: route({
    id: 'getRow',
    method: 'GET',
    path: '/rundowns/{rundownId}/rows/{rowId}',
    tag: 'rows',
    summary: 'Get one row',
    description: 'One row of the rundown, soft-deleted or not.',
    params: rowPathSchema,
    query: null,
    body: null,
    response: getRowResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  }),
  patchRows: route({
    id: 'patchRows',
    method: 'POST',
    path: '/rundowns/{rundownId}/patches',
    tag: 'rows',
    summary: 'Apply a batch of row patches',
    description: PATCHES_DESCRIPTION,
    params: rundownPathSchema,
    query: null,
    body: patchBatchRequestSchema,
    response: patchBatchResponseSchema,
    successStatus: 200,
    permission: 'rundown.edit',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  }),
  createPlaylist: route({
    id: 'createPlaylist',
    method: 'POST',
    path: '/playlists',
    tag: 'playlists',
    summary: 'Create a playlist',
    description:
      'Creates a playlist: an ordered set of rundowns with a timing mode (PLAN §5.1). Rundowns join it by setting their `parentId`; their `ord` is the running order. Supplying `playlistId` makes a retry idempotent.',
    params: null,
    query: null,
    body: createPlaylistRequestSchema,
    response: playlistResponseSchema,
    successStatus: 201,
    permission: 'rundown.edit',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'conflict'],
  }),
  listPlaylists: route({
    id: 'listPlaylists',
    method: 'GET',
    path: '/playlists',
    tag: 'playlists',
    summary: 'List playlists',
    description: 'Lists the tenant’s playlists the caller may read, in pages.',
    params: null,
    query: listPlaylistsQuerySchema,
    body: null,
    response: listPlaylistsResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request'],
  }),
  getPlaylist: route({
    id: 'getPlaylist',
    method: 'GET',
    path: '/playlists/{playlistId}',
    tag: 'playlists',
    summary: 'Get a playlist',
    description: 'The playlist’s head row and its partition cursor. Its rundowns: `GET /rundowns?playlistId=`.',
    params: playlistPathSchema,
    query: null,
    body: null,
    response: getPlaylistResponseSchema,
    successStatus: 200,
    permission: 'rundown.read',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  }),
  deletePlaylist: route({
    id: 'deletePlaylist',
    method: 'DELETE',
    path: '/playlists/{playlistId}',
    tag: 'playlists',
    summary: 'Soft-delete a playlist',
    description: 'Sets `deleted` on the playlist row. Its rundowns are not deleted; they materialise as roots of their own.',
    params: playlistPathSchema,
    query: null,
    body: null,
    response: playlistResponseSchema,
    successStatus: 200,
    permission: 'rundown.edit',
    permissionScope: 'resource',
    errors: ['bad-request', 'not-found'],
  }),
  patchPlaylist: route({
    id: 'patchPlaylist',
    method: 'POST',
    path: '/playlists/{playlistId}/patches',
    tag: 'playlists',
    summary: 'Apply a batch of patches to a playlist row',
    description: `The playlist row is the head of its own partition, so it is patched here rather than under a rundown. ${PATCHES_DESCRIPTION}`,
    params: playlistPathSchema,
    query: null,
    body: patchBatchRequestSchema,
    response: patchBatchResponseSchema,
    successStatus: 200,
    permission: 'rundown.edit',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  }),
  ...TENANCY_ROUTES,
  ...STATUS_ROUTES,
  ...OPERATIONS_ROUTES,
  ...SITES_ROUTES,
  ...OFFLINE_AUTH_ROUTES,
  ...REDUNDANCY_ROUTES,
  ...NODE_SYNC_ROUTES,
  ...CLOUD_PRODUCTIONS_ROUTES,
} as const;

/** Who may call a route (`RouteDef.auth`, with its default). */
export function routeAuth(route: Pick<RouteDef, 'auth'>): 'user' | 'node' | 'none' {
  return route.auth ?? 'user';
}

export type RouteId = keyof typeof ROUTES;

/** Every route, in table order. */
export function allRoutes(): readonly RouteDef[] {
  return Object.values(ROUTES);
}
